import React from 'react';
import { AsyncStorage } from 'services';
import { BootScreen } from 'screens';

// Types
type ThemeAction = { type: 'SET_THEME' | 'TOGGLE_THEME'; payload?: any };

type ThemeDispatch = (action: ThemeAction) => void;
type ThemeState = {
  theme: AppTheme;
};

const ThemeStateContext = React.createContext<ThemeState | undefined>(undefined);
const ThemeDispatchContext = React.createContext<ThemeDispatch | undefined>(undefined);

const getInitialState = async () => {
  const theme = (await AsyncStorage.get('theme')) as AppTheme;
  executeThemeEffects(theme);

  return {
    theme: theme || 'light',
  } as ThemeState;
};

const executeThemeEffects = (theme: AppTheme) => {
  document.body.classList.add('no-transitions');
  document.body.setAttribute('data-theme', theme);
  AsyncStorage.set('theme', theme);
  setTimeout(() => document.body.classList.remove('no-transitions'), 500);
};

export const themeReducer = (state: ThemeState, action: ThemeAction) => {
  switch (action.type) {
    case 'SET_THEME':
      executeThemeEffects(action.payload);
      return { ...state, theme: action.payload };
    case 'TOGGLE_THEME':
      const newTheme = state.theme === 'light' ? 'dark' : 'light';
      executeThemeEffects(newTheme);
      return { ...state, theme: newTheme };
    default:
      return state;
  }
};

export const ThemeProvider: React.FC<React.ReactNode> = ({ children }) => {
  const [data, setData] = React.useState<unknown>();

  React.useEffect(() => {
    getInitialState().then((data) => {
      setData(data);
    });
  }, []);

  if (!data) return <BootScreen />;

  return <Provider initialState={data}>{children}</Provider>;
};

const Provider: React.FC<{ children: React.ReactNode; initialState: unknown }> = ({
  children,
  initialState,
}: any) => {
  const [state, dispatch] = React.useReducer(themeReducer, initialState);

  return (
    <ThemeStateContext.Provider value={state}>
      <ThemeDispatchContext.Provider value={dispatch}>{children}</ThemeDispatchContext.Provider>
    </ThemeStateContext.Provider>
  );
};

export const useThemeState = () => {
  const context = React.useContext(ThemeStateContext);
  if (context === undefined) {
    throw new Error('useThemeState must be used within a ThemeProvider');
  }
  return context;
};

export const useThemeDispatch = () => {
  const context = React.useContext(ThemeDispatchContext);
  if (context === undefined) {
    throw new Error('useThemeDispatch must be used within a ThemeProvider');
  }
  return context;
};
